import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { Fragment, useState } from 'react';

import { Spacing, Typography } from 'chatie-design-system';

import Badge, { BadgeProps } from '@components/common/Badge';
import { OptimizeEFinderImage } from '@components/common/EFinderImage';
import SwipeableListContainer from '@components/common/SwipableListContainer';
import FreeChatEpisodeSelectModal from '@components/modal/FreeChatEpisodeSelectModal';

import { StoryTheme } from '@constants';
import { IStoryAd, IStoryDetail } from '@model';
import { media } from '@styles/css';
import { useWebView } from '@utils/webview';

import { StoryTag } from './Story';

export const storyWrapCSS = css`
  position: relative;
`;

export const storyActionCSS = css`
  top: 0;
  right: 0;
  padding-top: ${Spacing.small}px;
  padding-right: ${Spacing.small}px;
`;

const storyCardCSS = (theme: Theme) => css`
  display: inline-block;
  width: 120px;
  color: inherit;
  text-decoration: none;
  .title {
    ${Typography.h6};
  }
  .nickname {
    ${Typography.body12};

    color: ${theme.var.gray500};
  }
  .info-container {
    background: ${theme.var.white};
    border-radius: 0px 0px 4px 4px;
    padding: ${Spacing.xsmall}px;
    gap: ${Spacing.xxsmall}px;
  }
`;

const coverImageCSS = css`
  position: relative;
  width: 120px;
  height: 90px;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 4px 4px 4px 4px;
`;

const gridContainerCSS = (rows: number) => css`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(${rows}, 1fr);
  gap: 8px;
  padding: 8px 16px ${Spacing.small}px;

  ${media.tablet} {
    padding: 8px ${Spacing.xlarge}px ${Spacing.small}px;
  }
  ${media.desktop} {
    padding: 8px ${Spacing.xxlarge}px ${Spacing.small}px;
  }
`;

const badgeCSS = css`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0px;
  padding: 0px;
  border: none;
  z-index: 9;

  display: flex;
  justify-content: start;
  align-items: center;
  span {
    border-radius: 0 3px 3px 3px;
  }
`;

const freeChatCtaCPCSS = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 6px;
  padding: 8px 0;
  border-radius: 4px;
  width: 120px;
  cursor: pointer;
  background: ${theme.name === 'dark'
    ? 'linear-gradient(56.96deg, rgba(4, 200, 208, 0.12) 19.7%, rgba(21, 144, 212, 0.12) 80.3%)'
    : theme.var.white};
  ${theme.name !== 'dark' &&
  css`
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  `}

  span {
    ${Typography.body10};
    font-weight: 600;
    background: linear-gradient(56.96deg, #04c8d0 19.7%, #1590d4 80.3%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    white-space: nowrap;
  }
`;

type WFCPViewListProps = {
  stories: IStoryDetail[];
  hasAd: boolean;
  ad?: IStoryAd;
  badgePreset: Pick<BadgeProps, 'color' | 'backgroundColor' | 'border'>;
  showNativeAd?: boolean;
  showFreeChatCta?: boolean;
};

const WFCPViewList = ({
  stories,
  hasAd,
  ad,
  badgePreset,
  showFreeChatCta = false,
}: WFCPViewListProps) => {
  const { t } = useTranslation();
  const { openPageOrMove } = useWebView();
  const [selectedStory, setSelectedStory] = useState<IStoryDetail | null>(null);
  const [isFreeChatModalOpen, setFreeChatModalOpen] = useState(false);

  const handleClick = (url: string) => {
    openPageOrMove(url);
  };

  return (
    <SwipeableListContainer className="galleryCardListContainer">
      <div className="gridContainer" css={gridContainerCSS(1)}>
        {hasAd && !!ad?.story && (
          <a
            css={storyCardCSS}
            href={`/stories/${ad.story.id}`}
            onClick={(e) => {
              e.preventDefault();
              handleClick(`/stories/${ad.story.id}`);
            }}
            key={ad.id}
          >
            <div css={coverImageCSS} className="image-container">
              <div css={badgeCSS}>
                <Badge text={t('홍보')} {...badgePreset} />
              </div>
              <OptimizeEFinderImage
                image={ad.story?.coverImage}
                height={90}
                width={120}
                h={54}
              />
            </div>
            <div className="info-container">
              <p className="ellipsis title">{ad.story?.title}</p>
              <p className="ellipsis nickname">{ad.story?.user?.nickname}</p>
            </div>
          </a>
        )}
        {stories?.map((item) => {
          const { id, coverImage, title, user } = item;
          const storyUrl =
            item.theme === StoryTheme.iknow
              ? `/stories/${item.id}/licensed`
              : `/stories/${item.id}`;

          return (
            <Fragment key={id}>
              <a
                css={storyCardCSS}
                href={storyUrl}
                onClick={(e) => {
                  e.preventDefault();
                  handleClick(storyUrl);
                }}
              >
                <div css={coverImageCSS}>
                  <StoryTag {...item} />
                  <OptimizeEFinderImage
                    image={coverImage}
                    height={90}
                    width={120}
                    h={54}
                  />
                </div>
                <div className="info-container">
                  <p className="ellipsis title">{title}</p>
                  <p className="ellipsis nickname">{user?.nickname}</p>
                </div>
                {showFreeChatCta && (
                  <div
                    css={freeChatCtaCPCSS}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedStory(item);
                      setFreeChatModalOpen(true);
                    }}
                  >
                    <span>✨ {t('무료대화하기')}</span>
                  </div>
                )}
              </a>
            </Fragment>
          );
        })}
      </div>
      <FreeChatEpisodeSelectModal
        name="free-chat-episode-select-cp-modal"
        open={isFreeChatModalOpen}
        onClose={() => setFreeChatModalOpen(false)}
        story={selectedStory}
      />
    </SwipeableListContainer>
  );
};

export default WFCPViewList;
